// Transient notification cards — pinned to the top-right of the viewport,
// fade in, sit for a few seconds, fade out. Used for voyage-log events the
// player would otherwise miss while their attention is elsewhere on the page.

import type { VoyageEvent, VoyageEventKind } from '@realseas/shared';

export type ToastTone = 'good' | 'bad' | 'info' | 'warn';

const DEFAULT_TTL_MS = 5500;
const ENTER_MS = 220;
const EXIT_MS = 240;
const MAX_VISIBLE = 5;

interface ToastEntry {
  id: number;
  el: HTMLDivElement;
  timer: number;
}

const toasts: ToastEntry[] = [];
let nextId = 1;
let container: HTMLDivElement | null = null;

function ensureContainer(): HTMLDivElement {
  if (container) return container;
  container = document.createElement('div');
  container.className = 'toasts';
  document.body.appendChild(container);
  return container;
}

export function pushToast(text: string, tone: ToastTone = 'info', ttlMs: number = DEFAULT_TTL_MS): void {
  const c = ensureContainer();
  const el = document.createElement('div');
  el.className = `toast toast-${tone}`;
  el.textContent = text;
  const id = nextId++;
  const entry: ToastEntry = {
    id, el,
    timer: window.setTimeout(() => dismiss(id), ttlMs),
  };
  el.onclick = () => dismiss(id);
  c.appendChild(el);
  toasts.push(entry);
  // Trim the queue if it grows too tall — drop the oldest quickly.
  while (toasts.length > MAX_VISIBLE) {
    const oldest = toasts[0];
    dismiss(oldest.id);
  }
  // Trigger entry animation on next frame
  requestAnimationFrame(() => el.classList.add('in'));
}

function dismiss(id: number): void {
  const idx = toasts.findIndex(t => t.id === id);
  if (idx === -1) return;
  const t = toasts[idx];
  window.clearTimeout(t.timer);
  t.el.classList.remove('in');
  t.el.classList.add('out');
  toasts.splice(idx, 1);
  window.setTimeout(() => { t.el.remove(); }, EXIT_MS);
}

// Mapping of voyage-log event kinds → toast tone. Kinds NOT in this map are
// silent (still appear in Captain's Log, just no popup). Only the moments
// you'd want to glance up for.
const TOAST_TONES: Partial<Record<VoyageEventKind, ToastTone>> = {
  // Whaling
  whale_tethered: 'good',
  whale_harpooned_again: 'good',
  whale_lanced: 'good',
  whale_killed: 'good',
  whale_shot: 'good',
  whale_sounded: 'warn',
  whale_line_broke: 'bad',
  whale_cut_loose: 'warn',
  // Fishing
  rod_caught: 'good',
  rod_empty: 'info',
  net_hauled: 'good',
  net_broke: 'bad',
  net_repaired: 'info',
  // Crew morale
  crew_rum_ration: 'good',
  crew_swim: 'good',
  crew_rum_accident: 'bad',
  crew_swim_accident: 'bad',
  rum_ration: 'info',
  sunrise: 'info',
  sunset: 'info',
  cat_cuddles: 'info',
  morale_low: 'warn',
  // Crime
  crime_murder: 'bad',
  crime_theft: 'warn',
  // The pox (crew_interaction stays silent — log colour only)
  syphilis_contracted: 'warn',
  syphilis_spread: 'info',
  syphilis_insanity: 'bad',
  brig_confined: 'info',
  brig_released: 'info',
  walked_plank: 'bad',
  brig_rehabilitated: 'good',
  // Sightings — only the first time something new appears
  sighting_ship: 'info',
  sighting_pirates: 'warn',
  sighting_whale: 'info',
  sighting_fish: 'info',
  coast_sighted: 'warn',
  // Pirates
  pirates_bearing_down: 'bad',
  pirates_broke_off: 'good',
  // Hazards
  storm: 'bad',
  gale: 'warn',
  doldrums_entered: 'warn',
  doldrums_lifted: 'info',
  hull_low: 'bad',
  hull_repaired: 'good',
  food_low: 'warn',
  water_foul: 'warn',
  scurvy_warning: 'bad',
  scurvy_death: 'bad',
  starvation_death: 'bad',
  thirst_death: 'bad',
  // Combat
  volley_fired: 'good',
  volley_received: 'bad',
  ship_sunk_enemy: 'good',
  boarded_enemy: 'good',
  boarded_by_enemy: 'bad',
  boarding_victory: 'good',
  boarding_defeat: 'bad',
  // Trade
  trade_received: 'info',
  trade_proposed: 'info',
  trade_accepted: 'good',
  trade_declined: 'warn',
  // Discovery / endings
  shipwreck_found: 'good',
  arrived: 'good',
  ran_aground: 'bad',
  mutiny: 'bad',
};

export function toastForEvent(ev: VoyageEvent): void {
  const tone = TOAST_TONES[ev.kind];
  if (!tone) return;
  pushToast(ev.text, tone);
}
